import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo } from 'react';

import { Box, Button, CircularProgress } from '@mui/material';

import useStores from 'hooks/use-stores';

import useStyles from './styles';

const ALL = 'All';

interface Props {
    selected: number;
    onSelect: (year: number | null) => void;
    getOptionHref: (year: number | null) => string;
    className?: string;
}

const YearFilter: React.FC<Props> = ({ selected, onSelect, getOptionHref, className }) => {
    const { classes, cx } = useStyles();

    const { cveLineChartStore } = useStores();

    useEffect(() => {
        void cveLineChartStore.loadData({ termName: 'type', termValue: 'cve' });
    }, [cveLineChartStore]);

    const handleSelect = useCallback(
        (event) => {
            event.preventDefault();
            const { option } = event.currentTarget.dataset;
            const value = option?.toLowerCase() ?? null;
            onSelect && onSelect(value);
        },
        [onSelect]
    );

    const years = useMemo(() => {
        const output = new Set<number>();
        Object.values(cveLineChartStore.data).forEach((stats: Record<number, number>) =>
            Object.keys(stats).forEach((year) => output.add(Number(year)))
        );
        return [...output].sort((a, b) => a - b);
    }, [cveLineChartStore.data]);

    const options = useMemo(
        () => [{ label: ALL, value: null }, ...years.map((year) => ({ label: year, value: year }))],
        [years]
    );

    return (
        <Box className={cx(classes.container, className)}>
            {cveLineChartStore.isLoading ? (
                <Box className={classes.spinner}>
                    <CircularProgress />
                </Box>
            ) : (
                options.map((option) => {
                    const additional = getOptionHref ? { component: 'a', href: getOptionHref(option.value) } : {};
                    return (
                        <Button
                            key={option.label}
                            className={cx(classes.button, { [classes._selected]: option.value === (selected || null) })}
                            variant="outlined"
                            data-option={option.value}
                            onClick={handleSelect}
                            {...additional}
                        >
                            {option.label}
                        </Button>
                    );
                })
            )}
        </Box>
    );
};

export default observer(YearFilter);
